import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  IconBook2,
  IconBuilding,
  IconCreditCard,
  IconFileText,
  IconKey,
  IconLayoutDashboard,
  IconMenu2,
  IconRoute,
  IconSettings,
  IconTargetArrow,
  IconTrendingUp,
  IconWebhook,
} from "@tabler/icons-react";
import s from "./partnerPortal.module.css";
import { Logo } from "../../constants";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  PartnerContextProvider,
  usePartnerContext,
} from "../../context/partnerContext";
import { getPartnerToken, clearPartnerSession } from "../../services/partnerCalls";

const NAV = [
  {
    id: "dashboard",
    label: "Dashboard",
    Icon: IconLayoutDashboard,
    to: "/partner/dashboard",
  },
  { id: "api-keys", label: "API Keys", Icon: IconKey, to: "/partner/api-keys" },
  { id: "usage", label: "Usage", Icon: IconTrendingUp, to: "/partner/usage" },
  {
    id: "sessions",
    label: "Test Sessions",
    Icon: IconFileText,
    to: "/partner/sessions",
  },
  {
    id: "candidate-fit",
    label: "Candidate Fit",
    Icon: IconTargetArrow,
    to: "/partner/candidate-fit",
  },
  {
    id: "webhooks",
    label: "Webhooks",
    Icon: IconWebhook,
    to: "/partner/webhooks",
  },
  {
    id: "billing",
    label: "Billing & Credits",
    Icon: IconCreditCard,
    to: "/partner/billing",
  },
  {
    id: "how-it-works",
    label: "How it works",
    Icon: IconRoute,
    to: "/partner/how-it-works",
  },
  { id: "docs", label: "Docs", Icon: IconBook2, to: "/partner/docs" },
  {
    id: "settings",
    label: "Settings",
    Icon: IconSettings,
    to: "/partner/settings",
  },
];

const PartnerPortalContent = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(900);
  const [navOpen, setNavOpen] = useState(false);
  const { profileData } = usePartnerContext();

  const balance = profileData?.balance ?? 0;
  const allocation = Math.max(profileData?.allocation ?? 0, balance, 1);
  const pct = Math.round((balance / allocation) * 100);

  // Leaving mobile should never strand the drawer open.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);


  return (
    <div className={s.page}>
      {isMobile && navOpen && (
        <div className={s.sidebarBackdrop} onClick={() => setNavOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`${s.sidebar} ${navOpen ? s.sidebarOpen : ""}`}>
        <Link to="/api-integrations" className={s.sidebarBrand}>
          <Logo color="#f1f5ff" />
        </Link>
        <span className={s.brandTag}>PARTNER PORTAL</span>

        {NAV.map(({ id, label, Icon, to }) => (
          <NavLink
            key={id}
            className={({ isActive }) =>
              isActive ? `${s.navBtn} ${s.navBtnActive}` : s.navBtn
            }
            to={to}
          >
            <span className={s.navIcon}>
              <Icon size={15} />
            </span>
            {label}
          </NavLink>
        ))}

        <div className={s.balanceCard}>
          <div className={s.balanceLabel}>Balance</div>
          <div className={s.balanceValue}>
            {profileData?.balance?.toLocaleString() || 0}
            <span className={s.balanceUnit}> cr</span>
          </div>
          <div className={s.balanceTrack}>
            <div className={s.balanceFill} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className={s.main}>
        <header className={s.topbar}>
          <div className={s.topbarLeft}>
            <button
              type="button"
              className={s.mobileNavBtn}
              onClick={() => setNavOpen((p) => !p)}
              aria-label="Toggle navigation"
            >
              <IconMenu2 size={17} />
            </button>
            <span>Partner ·</span>
            <span className={s.topbarName}>
              {profileData?.branding?.brandName}
            </span>
            {profileData?.tier && (
              <span className={s.tierPill}>{profileData?.tier} tier</span>
            )}
          </div>

          <div className={s.topbarRight}>
            <span className={s.topbarMail}>{profileData?.email || ""}</span>
            <span className={s.topbarAvatar}>
              <IconBuilding size={15} />
            </span>
            <button
              type="button"
              className={s.signOut}
              onClick={() => {
                clearPartnerSession();
                navigate("/partner-login", { replace: true });
              }}
            >
              Sign out
            </button>
          </div>
        </header>

        <div className={s.content}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

const PartnerPortal = () => {
  // Gate on the partner's own isolated session, never the main platform's
  // client/talent login — a logged-in client must not get into the partner
  // console just by navigating here. Checked synchronously before anything
  // renders so an unauthenticated visitor never sees the shell.
  if (!getPartnerToken()) {
    return <Navigate to="/partner-login" replace />;
  }

  return (
    <PartnerContextProvider>
      <PartnerPortalContent />
    </PartnerContextProvider>
  );
};

export default PartnerPortal;
